import * as THREE from 'three';

export const CORE_SPLIT_DURATION = 1.1;
export const CORE_EXPLOSION_DURATION = 2.8;
export const CORE_DESTRUCTION_DURATION_MS =
  (CORE_SPLIT_DURATION + CORE_EXPLOSION_DURATION) * 1000 + 200;

// Clip the actual Core triangles at the center plane and close both cut faces.
// The halves retain the Core's silhouette and its orientation at impact.
const splitGeometry = (geometry) => {
  const positions = geometry.getAttribute('position');
  const boundary = new Map();
  const rememberBoundary = (vertex) => {
    if (Math.abs(vertex.x) > 0.000001) return;
    const point = vertex.clone().setX(0);
    boundary.set(`${point.y.toFixed(6)},${point.z.toFixed(6)}`, point);
  };
  const bodies = [-1, 1].map((side) => {
    const values = [];
    for (let index = 0; index < positions.count; index += 3) {
      const triangle = [0, 1, 2].map((offset) =>
        new THREE.Vector3().fromBufferAttribute(positions, index + offset)
      );
      const polygon = [];
      triangle.forEach((start, edge) => {
        const end = triangle[(edge + 1) % 3];
        const startInside = start.x * side >= 0;
        const endInside = end.x * side >= 0;
        if (startInside) polygon.push(start);
        rememberBoundary(start);
        if (startInside !== endInside) {
          const crossing = start.clone().lerp(end, start.x / (start.x - end.x));
          crossing.x = 0;
          polygon.push(crossing);
          rememberBoundary(crossing);
        }
      });
      for (let vertex = 1; vertex < polygon.length - 1; vertex += 1) {
        [polygon[0], polygon[vertex], polygon[vertex + 1]].forEach((point) =>
          values.push(...point.toArray())
        );
      }
    }
    const body = new THREE.BufferGeometry();
    body.setAttribute('position', new THREE.Float32BufferAttribute(values, 3));
    body.computeVertexNormals();
    return body;
  });
  const rim = [...boundary.values()].sort(
    (a, b) => Math.atan2(a.z, a.y) - Math.atan2(b.z, b.y)
  );
  return bodies.map((body, index) => {
    const values = [];
    rim.forEach((point, edge) => {
      const next = rim[(edge + 1) % rim.length];
      const triangle = index === 0 ? [point, next] : [next, point];
      values.push(0, 0, 0, ...triangle[0].toArray(), ...triangle[1].toArray());
    });
    const cut = new THREE.BufferGeometry();
    cut.setAttribute('position', new THREE.Float32BufferAttribute(values, 3));
    cut.computeVertexNormals();
    return { body, cut };
  });
};

export const createCoreDestruction = (source, scene) => {
  const group = new THREE.Group();
  group.visible = false;
  scene.add(group);
  const halves = [-1, 1].map((side) => {
    const half = new THREE.Group();
    const body = new THREE.Mesh(
      new THREE.BufferGeometry(),
      source.material.clone()
    );
    body.castShadow = true;
    const cut = new THREE.Mesh(
      new THREE.BufferGeometry(),
      new THREE.MeshPhysicalMaterial({
        color: 0xffe8b2,
        emissive: 0xff794b,
        emissiveIntensity: 3.5,
        roughness: 0.25,
        metalness: 0.4,
        transparent: true,
      })
    );
    const wire = new THREE.LineSegments(
      new THREE.BufferGeometry(),
      new THREE.LineBasicMaterial({
        color: 0x9dffff,
        transparent: true,
        opacity: 0.9,
      })
    );
    half.add(body, cut, wire);
    group.add(half);
    return { group: half, body, cut, wire, side };
  });
  const slash = new THREE.Group();
  [
    [0.3, 0xff5eaa, 0.35],
    [0.12, 0x68ffff, 0.7],
    [0.028, 0xffffff, 1],
  ].forEach(([width, color, opacity], index) => {
    const streak = new THREE.Mesh(
      new THREE.PlaneGeometry(width, 1),
      new THREE.MeshBasicMaterial({
        color,
        opacity,
        transparent: true,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
        depthTest: false,
      })
    );
    streak.position.z = 0.8 + index * 0.005;
    streak.userData.opacity = opacity;
    slash.add(streak);
  });
  group.add(slash);

  const start = (activeSource = source) => {
    activeSource.updateWorldMatrix(true, false);
    activeSource.getWorldPosition(group.position);
    const rotation = activeSource.getWorldQuaternion(new THREE.Quaternion());
    const scale = activeSource.getWorldScale(new THREE.Vector3());
    const geometry = activeSource.geometry.index
      ? activeSource.geometry.toNonIndexed()
      : activeSource.geometry.clone();
    geometry.applyMatrix4(
      new THREE.Matrix4().compose(new THREE.Vector3(), rotation, scale)
    );
    const pieces = splitGeometry(geometry);
    geometry.dispose();
    halves.forEach(({ group: half, body, cut, wire }, index) => {
      body.geometry.dispose();
      cut.geometry.dispose();
      wire.geometry.dispose();
      body.geometry = pieces[index].body;
      cut.geometry = pieces[index].cut;
      wire.geometry = new THREE.EdgesGeometry(body.geometry, 8);
      body.material.copy(activeSource.material);
      body.material.transparent = true;
      half.position.set(0, 0, 0);
      half.rotation.set(0, 0, 0);
      half.scale.setScalar(1);
    });
    group.visible = true;
    update(0);
  };

  const update = (elapsed, reduceMotion = false) => {
    const slice = THREE.MathUtils.clamp(elapsed / 0.32, 0, 1);
    const length = Math.max(0.001, slice * 2.8);
    const slashFade = 1 - THREE.MathUtils.smoothstep(elapsed, 0.35, 0.95);
    slash.visible = !reduceMotion && slashFade > 0;
    slash.children.forEach((streak) => {
      streak.position.y = 1.4 - length / 2;
      streak.scale.y = length;
      streak.material.opacity = streak.userData.opacity * slashFade;
    });
    const tear = THREE.MathUtils.clamp((elapsed - 0.28) / 0.82, 0, 1);
    const separation = 1 - Math.pow(1 - tear, 3);
    const blastElapsed = Math.max(0, elapsed - CORE_SPLIT_DURATION);
    const blast = THREE.MathUtils.clamp(
      blastElapsed / CORE_EXPLOSION_DURATION,
      0,
      1
    );
    const fade = 1 - THREE.MathUtils.smoothstep(blastElapsed, 0.05, 0.85);
    halves.forEach(({ group: half, body, cut, wire, side }) => {
      half.position.set(
        side * (separation * 0.95 + blastElapsed * 1.15),
        Math.sin(tear * Math.PI) * 0.12 - blastElapsed * blastElapsed * 0.35,
        0
      );
      half.rotation.set(
        blastElapsed * side * 2.2,
        side * (separation * 0.5 + blastElapsed * 3.5),
        -side * (separation * 0.16 + blastElapsed * 1.8)
      );
      half.scale.setScalar(
        (1 + Math.sin(blast * Math.PI) * 0.2) * Math.max(0.03, 1 - blast * 2)
      );
      half.visible = fade > 0;
      body.material.opacity = 0.95 * fade;
      body.material.emissiveIntensity = 2.2 + separation * 2;
      cut.material.opacity = fade;
      cut.material.emissiveIntensity = 3.5 + separation * 4;
      wire.material.opacity = 0.9 * fade;
    });
  };

  const getShardOrigin = (index, target) => {
    target.copy(halves[index % 2].group.position);
    return group.localToWorld(target);
  };

  return { group, halves, start, update, getShardOrigin };
};
